import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Star, Tag, Mail, MailOpen, Trash2, Archive, ArchiveRestore, PanelRightOpen, PanelRightClose } from 'lucide-react'
import ProfilePhoto from '../../../../../components/ProfilePhoto'
import SvgFragment from '../../../../../fragments/SvgFragment'
import TooltipWrapper from '../../../../../components/TooltipWrapper'
import { useInboxStore } from '../../../../../stores/inbox/useInboxStore'
import { useInboxHandlers } from '../../../../../services/actions/inbox/useInboxHandlers'
import { useAuthStore } from '../../../../../stores/auth/useAuthStore'
import { useAppHandlers } from '../../../../../services/actions/app/useAppHandlers'
import { linkedinUrlFromParticipant, normalizeLinkedinUrl } from '../hubspotInboxExportUtils'
import { useInboxModals } from '../useInboxModals'
import { openInboxBulkTags } from '../openInboxBulkTags'

const LinkedInExternalIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
)

const PinIcon: React.FC<{ active?: boolean }> = ({ active = false }) => {
    if (active) {
        // Match filled pin used in ConversationsList (ConversationItem.tsx)
        return (
            <svg width="22" height="22" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                {/* Scale up slightly so it matches other 22×22 action icons visually */}
                <g transform="translate(8 8) scale(1.12) translate(-8 -8)">
                    <path
                        fill="#FF9100"
                        fillRule="evenodd"
                        d="m.837 14.845.533-1.598a.298.298 0 0 1 .06-.098l2.309-2.308L.72 7.822a.753.753 0 0 1 0-1.065c1.136-1.136 2.871-1.186 4.432-.616l2.896-2.353-.937-.937a.753.753 0 0 1 0-1.065L8.178.72a.753.753 0 0 1 1.065 0l6.036 6.036a.753.753 0 0 1 0 1.065l-1.065 1.066a.753.753 0 0 1-1.065 0l-.937-.937-2.353 2.896c.576 1.577.51 3.306-.616 4.432a.753.753 0 0 1-1.065 0l-3.019-3.018-2.308 2.308a.251.251 0 0 1-.098.061l-1.598.533a.251.251 0 0 1-.318-.318z"
                    />
                </g>
            </svg>
        )
    }

    // Outline pin (16×16 glyph inside 22×22 slot)
    return (
        <svg width="22" height="22" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
            <path
                d="M4.90781 10.5922L0.75 14.75M7.34633 3.99587L6.19907 5.14313C6.10549 5.23671 6.05869 5.2835 6.00538 5.32069C5.95806 5.35369 5.90702 5.381 5.85332 5.40207C5.79281 5.4258 5.72792 5.43878 5.59814 5.46474L2.90474 6.00342C2.2048 6.14341 1.85482 6.2134 1.69109 6.39793C1.54845 6.55868 1.48332 6.77381 1.51283 6.98669C1.54671 7.23104 1.79908 7.48341 2.30382 7.98815L7.51186 13.1962C8.0166 13.701 8.26897 13.9533 8.51333 13.9872C8.7262 14.0167 8.94133 13.9516 9.10209 13.8089C9.28661 13.6452 9.35661 13.2952 9.4966 12.5953L10.0353 9.90188C10.0612 9.7721 10.0742 9.70722 10.0979 9.64671C10.119 9.593 10.1463 9.54196 10.1793 9.49464C10.2165 9.44133 10.2633 9.39454 10.3569 9.30095L11.5041 8.15369C11.564 8.09386 11.5939 8.06394 11.6268 8.03782C11.656 8.01462 11.6869 7.99367 11.7193 7.97516C11.7558 7.95433 11.7947 7.93766 11.8725 7.90433L13.7058 7.1186C14.2407 6.88937 14.5081 6.77476 14.6296 6.58955C14.7358 6.42759 14.7738 6.23024 14.7353 6.04041C14.6913 5.82333 14.4856 5.61759 14.0741 5.20611L10.2939 1.42587C9.88242 1.0144 9.67669 0.808661 9.45961 0.764655C9.26977 0.726173 9.07243 0.764179 8.91046 0.870411C8.72525 0.99189 8.61064 1.25932 8.38141 1.79419L7.59569 3.62755C7.56235 3.70533 7.54569 3.74422 7.52486 3.78068C7.50635 3.81308 7.4854 3.84402 7.4622 3.87323C7.43608 3.90612 7.40616 3.93603 7.34633 3.99587Z"
                stroke="#8C96AD"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )
}

const ContactDetails: React.FC = () => {
    const { store: { selectedConversation, conversationDetails, selectedConnection, selectedTab, messages }, dispatch: inboxDispatch } = useInboxStore()
    const { store: { workspace } } = useAuthStore()
    const {
        starConversationHandler,
        pinConversationHandler,
        readConversationHandler,
        archiveConversationHandler,
        visitParticipantLinkedInProfileHandler,
        openConversationDetailsHandler,
    } = useInboxHandlers()
    const { setBulkEditTagsModal, showInfoToast } = useAppHandlers()
    const { openHubspotExportModal } = useInboxModals()

    const hubspotEnabled = workspace?.hubspotEnabled === true
    const isSingle = selectedConversation?.participants?.length === 1
    const isSnTab = selectedTab === 'SalesNavigator'
    const canExportToHubspot = (() => {
        if (!hubspotEnabled || !selectedConversation) return false
        const conversationId = selectedConversation.conversationId || selectedConversation._id
        const userEmail = selectedConnection?.email
        let linkedinUrl = selectedConnection?.linkedinUrl
        if (!linkedinUrl && isSingle) {
            linkedinUrl = linkedinUrlFromParticipant(selectedConversation.participants?.[0])
        }
        return Boolean(conversationId && (userEmail || linkedinUrl || isSnTab))
    })()
    const [menuOpen, setMenuOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const [compactActions, setCompactActions] = useState(false)

    // Collapse actions into the 3-dot menu when this header container gets narrow.
    // We avoid CSS @container queries because production CSS minifiers/build steps sometimes drop them.
    useLayoutEffect(() => {
        const el = containerRef.current
        if (!el) return

        const THRESHOLD = 560
        let raf = 0

        const update = (width: number) => {
            // rAF to avoid ResizeObserver -> setState -> layout feedback loops
            if (raf) cancelAnimationFrame(raf)
            raf = requestAnimationFrame(() => setCompactActions(width <= THRESHOLD))
        }

        // Initial measurement
        update(el.getBoundingClientRect().width)

        if (typeof ResizeObserver === 'undefined') {
            const onResize = () => update(el.getBoundingClientRect().width)
            window.addEventListener('resize', onResize)
            return () => {
                if (raf) cancelAnimationFrame(raf)
                window.removeEventListener('resize', onResize)
            }
        }

        const ro = new ResizeObserver(entries => {
            const entry = entries[0]
            if (!entry) return
            update(entry.contentRect.width)
        })
        ro.observe(el)

        return () => {
            if (raf) cancelAnimationFrame(raf)
            ro.disconnect()
        }
    }, [])

    useEffect(() => {
        if (!menuOpen) return
        const onDown = (e: MouseEvent) => {
            if (!menuRef.current) return
            if (menuRef.current.contains(e.target as Node)) return
            setMenuOpen(false)
        }
        document.addEventListener('mousedown', onDown)
        return () => document.removeEventListener('mousedown', onDown)
    }, [menuOpen])

    const formatName = (p: any) => [p?.firstName, p?.lastName].filter(Boolean).join(' ') || p?.publicIdentifier || 'Unknown'

    const getDisplayName = () => {
        if (isSingle) return formatName(selectedConversation.participants[0])
        return selectedConversation?.conversationName || 'Group conversation'
    }

    const getOccupation = () => {
        if (isSingle) {
            return (
                selectedConversation?.participants?.[0]?.occupation ||
                selectedConnection?.headline ||
                ''
            )
        }
        return selectedConversation?.participants?.map((p: any) => formatName(p)).join(', ') || ''
    }

    const handleDelete = () => {
        inboxDispatch({ type: 'set-modal', payload: { type: 'deleteConversation', props: { conversationId: selectedConversation?.conversationId, conversationExistsOnLinkedIn: true } } })
    }

    const handleTag = () => {
        const id = selectedConnection?._id || selectedConversation?.connectionId
        const tags =
            (Array.isArray(selectedConversation?.tags) && selectedConversation.tags) ||
            (Array.isArray(selectedConnection?.tags) && selectedConnection.tags) ||
            []
        openInboxBulkTags(id, tags, setBulkEditTagsModal, showInfoToast)
    }

    const handleHubspotExport = () => {
        if (!selectedConversation || !canExportToHubspot) return
        const conversationId = selectedConversation.conversationId || selectedConversation._id
        const userEmail = selectedConnection?.email
        let linkedinUrl = selectedConnection?.linkedinUrl
        if (!linkedinUrl && isSingle) {
            linkedinUrl = linkedinUrlFromParticipant(selectedConversation.participants?.[0])
        }
        openHubspotExportModal({
            conversationId,
            userEmail: userEmail ?? null,
            linkedinUrl: normalizeLinkedinUrl(linkedinUrl),
            isSales: isSnTab,
            connectionId: selectedConversation.connectionId,
            participants: [...(selectedConversation.participants || [])],
            messages: [...messages],
        })
    }

    if (!selectedConversation) return null

    return (
        <Container ref={containerRef}>
            <ProfileSection>
                {isSingle ? (
                    <ClickablePhoto onClick={() => visitParticipantLinkedInProfileHandler(selectedConversation.participants[0])}>
                        <ProfilePhoto
                            source={selectedConversation.participants[0]?.profilePicture}
                            capitalLetters={[selectedConversation.participants[0]?.firstName?.[0], selectedConversation.participants[0]?.lastName?.[0]].filter(Boolean).join('') || 'NN'}
                            allowTooltip={false}
                            width="40px"
                            height="40px"
                        />
                    </ClickablePhoto>
                ) : (
                    <MultiplePeople>
                        <PhotoTopRight>
                            <ProfilePhoto source={selectedConversation.participants?.[0]?.profilePicture} capitalLetters={[selectedConversation.participants?.[0]?.firstName?.[0], selectedConversation.participants?.[0]?.lastName?.[0]].filter(Boolean).join('') || 'NN'} allowTooltip={false} width="24px" height="24px" />
                        </PhotoTopRight>
                        <PhotoBottomLeft>
                            <ProfilePhoto source={selectedConversation.participants?.[1]?.profilePicture} capitalLetters={[selectedConversation.participants?.[1]?.firstName?.[0], selectedConversation.participants?.[1]?.lastName?.[0]].filter(Boolean).join('') || 'NN'} allowTooltip={false} width="24px" height="24px" />
                        </PhotoBottomLeft>
                    </MultiplePeople>
                )}
                <NameSection>
                    <NameRow>
                        <Name
                            clickable={isSingle}
                            onClick={isSingle ? () => visitParticipantLinkedInProfileHandler(selectedConversation.participants[0]) : undefined}
                        >
                            {getDisplayName()}
                        </Name>
                        {isSingle && selectedConversation.participants[0]?.publicIdentifier && (
                            <TooltipWrapper tooltipText="Open LinkedIn profile" placement="top">
                                <LiIconBtn
                                    onClick={() => visitParticipantLinkedInProfileHandler(selectedConversation.participants[0])}
                                >
                                    <LinkedInExternalIcon />
                                </LiIconBtn>
                            </TooltipWrapper>
                        )}
                    </NameRow>
                    {getOccupation() && <Occupation>{getOccupation()}</Occupation>}
                </NameSection>
            </ProfileSection>

            <ActionsRow $hidden={compactActions}>
                <ActionGroup>
                    <TooltipWrapper tooltipText={selectedConversation.isStarred ? 'Unstar (S)' : 'Star (S)'}>
                        <ActionBtn
                            $active={selectedConversation.isStarred}
                            disabled={isSnTab}
                            onClick={() => !isSnTab && starConversationHandler({ conversationId: selectedConversation.conversationId, isStarred: !selectedConversation.isStarred })}
                        >
                            <Star size={14} fill={selectedConversation.isStarred ? '#FDBE00' : 'none'} color={selectedConversation.isStarred ? '#FDBE00' : '#8c96ad'} />
                        </ActionBtn>
                    </TooltipWrapper>

                    <TooltipWrapper tooltipText={selectedConversation.isPinned ? 'Unpin (P)' : 'Pin (P)'}>
                        <ActionBtn
                            $active={selectedConversation.isPinned}
                            disabled={isSnTab}
                            onClick={() => !isSnTab && pinConversationHandler({ conversationId: selectedConversation.conversationId, isPinned: !selectedConversation.isPinned })}
                        >
                            <PinIcon active={selectedConversation.isPinned} />
                        </ActionBtn>
                    </TooltipWrapper>

                    <TooltipWrapper tooltipText="Tag (# / Ctrl+Shift+A)">
                        <ActionBtn onClick={handleTag}>
                            <Tag size={14} color="#8c96ad" />
                        </ActionBtn>
                    </TooltipWrapper>

                    <TooltipWrapper tooltipText={selectedConversation.isRead ? 'Mark as Unread (I)' : 'Mark as Read (I)'}>
                        <ActionBtn
                            disabled={isSnTab}
                            onClick={() => !isSnTab && readConversationHandler(selectedConversation, !selectedConversation.isRead)}
                        >
                            {selectedConversation.isRead
                                ? <MailOpen size={14} color="#8c96ad" />
                                : <Mail size={14} color="#8c96ad" />
                            }
                        </ActionBtn>
                    </TooltipWrapper>

                    <TooltipWrapper tooltipText={canExportToHubspot ? 'Export to HubSpot' : hubspotEnabled ? 'Missing contact info for HubSpot export' : 'HubSpot not enabled'}>
                        <ActionBtn onClick={canExportToHubspot ? handleHubspotExport : undefined} disabled={!canExportToHubspot}>
                            <SvgFragment type="exportHubspot" />
                        </ActionBtn>
                    </TooltipWrapper>

                    <TooltipWrapper tooltipText={selectedConversation.isArchived ? 'Unarchive (E)' : 'Archive (E)'}>
                        <ActionBtn
                            $active={selectedConversation.isArchived}
                            disabled={isSnTab}
                            onClick={() => !isSnTab && archiveConversationHandler({ id: selectedConversation._id, conversationId: selectedConversation.conversationId, isArchived: !selectedConversation.isArchived })}
                        >
                            {selectedConversation.isArchived
                                ? <ArchiveRestore size={14} color="#8c96ad" />
                                : <Archive size={14} color="#8c96ad" />
                            }
                        </ActionBtn>
                    </TooltipWrapper>

                    <TooltipWrapper tooltipText="Delete">
                        <ActionBtn disabled={isSnTab} onClick={() => !isSnTab && handleDelete()}>
                            <Trash2 size={14} color="#8c96ad" />
                        </ActionBtn>
                    </TooltipWrapper>
                </ActionGroup>
            </ActionsRow>

            <MenuWrap ref={menuRef} $visible={compactActions}>
                <TooltipWrapper tooltipText="More actions">
                    <MenuBtn onClick={() => setMenuOpen(o => !o)} aria-expanded={menuOpen} aria-haspopup="menu">
                        <SvgFragment type="threeDots" />
                    </MenuBtn>
                </TooltipWrapper>
                {menuOpen && (
                    <MenuDropdown role="menu">
                        <MenuItem
                            role="menuitem"
                            aria-disabled={isSnTab}
                            $disabled={isSnTab}
                            onClick={() => {
                                if (isSnTab) return
                                starConversationHandler({ conversationId: selectedConversation.conversationId, isStarred: !selectedConversation.isStarred })
                                setMenuOpen(false)
                            }}
                        >
                            <Star size={14} fill={selectedConversation.isStarred ? '#FDBE00' : 'none'} color={selectedConversation.isStarred ? '#FDBE00' : '#8c96ad'} />
                            {selectedConversation.isStarred ? 'Unstar' : 'Star'}
                        </MenuItem>

                        <MenuItem
                            role="menuitem"
                            aria-disabled={isSnTab}
                            $disabled={isSnTab}
                            onClick={() => {
                                if (isSnTab) return
                                pinConversationHandler({ conversationId: selectedConversation.conversationId, isPinned: !selectedConversation.isPinned })
                                setMenuOpen(false)
                            }}
                        >
                            <PinIcon active={selectedConversation.isPinned} />
                            {selectedConversation.isPinned ? 'Unpin' : 'Pin'}
                        </MenuItem>

                        <MenuItem role="menuitem" onClick={() => { handleTag(); setMenuOpen(false) }}>
                            <Tag size={14} color="#8c96ad" />
                            Apply / Remove tags
                        </MenuItem>

                        <MenuItem
                            role="menuitem"
                            aria-disabled={isSnTab}
                            $disabled={isSnTab}
                            onClick={() => {
                                if (isSnTab) return
                                readConversationHandler(selectedConversation, !selectedConversation.isRead)
                                setMenuOpen(false)
                            }}
                        >
                            {selectedConversation.isRead ? <MailOpen size={14} color="#8c96ad" /> : <Mail size={14} color="#8c96ad" />}
                            {selectedConversation.isRead ? 'Mark as Unread' : 'Mark as Read'}
                        </MenuItem>

                        <MenuItem
                            role="menuitem"
                            aria-disabled={!canExportToHubspot}
                            $disabled={!canExportToHubspot}
                            onClick={() => {
                                if (!canExportToHubspot) return
                                handleHubspotExport()
                                setMenuOpen(false)
                            }}
                        >
                            <SvgFragment type="exportHubspot" />
                            Export to HubSpot
                        </MenuItem>

                        <MenuItem
                            role="menuitem"
                            aria-disabled={isSnTab}
                            $disabled={isSnTab}
                            onClick={() => {
                                if (isSnTab) return
                                archiveConversationHandler({ id: selectedConversation._id, conversationId: selectedConversation.conversationId, isArchived: !selectedConversation.isArchived })
                                setMenuOpen(false)
                            }}
                        >
                            {selectedConversation.isArchived ? <ArchiveRestore size={14} color="#8c96ad" /> : <Archive size={14} color="#8c96ad" />}
                            {selectedConversation.isArchived ? 'Unarchive' : 'Archive'}
                        </MenuItem>

                        <MenuItem
                            role="menuitem"
                            aria-disabled={isSnTab}
                            $disabled={isSnTab}
                            $danger
                            onClick={() => {
                                if (isSnTab) return
                                handleDelete()
                                setMenuOpen(false)
                            }}
                        >
                            <Trash2 size={14} color="#8c96ad" />
                            Delete
                        </MenuItem>
                    </MenuDropdown>
                )}
            </MenuWrap>

            {isSingle && (
                <SidebarToggleWrap>
                    <ActionsDivider />
                    <TooltipWrapper tooltipText={conversationDetails ? 'Close Sidebar' : 'Open Sidebar'}>
                        <ActionBtn
                            $active={conversationDetails}
                            onClick={() => openConversationDetailsHandler(!conversationDetails)}
                        >
                            {conversationDetails ? (
                                <PanelRightClose size={22} color="#5964ff" />
                            ) : (
                                <PanelRightOpen size={22} color="#8c96ad" />
                            )}
                        </ActionBtn>
                    </TooltipWrapper>
                </SidebarToggleWrap>
            )}
        </Container>
    )
}

const Container = styled.div`
    width: 100%;
    padding: 12px 16px;
    background-color: #ffffff;
    border-bottom: 1px solid #dae0e8;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    font-family: 'Nunito Sans', sans-serif;
`
const ProfileSection = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
    overflow: hidden;
`
const ClickablePhoto = styled.div`
    cursor: pointer;
    flex-shrink: 0;
    &:hover { opacity: 0.85; }
`
const MultiplePeople = styled.div`
    width: 40px;
    height: 40px;
    position: relative;
    flex-shrink: 0;
`
const PhotoTopRight = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    border: 1px solid white;
    border-radius: 50%;
    overflow: hidden;
`
const PhotoBottomLeft = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    border: 1px solid white;
    border-radius: 50%;
    overflow: hidden;
`
const NameSection = styled.div`
    flex: 1;
    min-width: 0;
`
const NameRow = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
`
const LiIconBtn = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #0077b5;
    opacity: 0.6;
    flex-shrink: 0;
    transition: opacity 0.15s;
    &:hover { opacity: 1; }
`
const Name = styled.p<{ clickable?: boolean }>`
    font-size: 14px;
    font-weight: 700;
    line-height: 21px;
    color: #333951;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    margin: 0;
    min-width: 0;
    cursor: ${props => props.clickable ? 'pointer' : 'default'};
    &:hover { color: ${props => props.clickable ? '#5964ff' : 'inherit'}; }
`
const Occupation = styled.p`
    font-size: 12px;
    line-height: 14.4px;
    color: #6a7181;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    margin: 0;
`

const ActionsDivider = styled.div`
    width: 1px;
    height: 22px;
    background: #dae0e8;
    flex-shrink: 0;
`
const ActionsRow = styled.div<{ $hidden?: boolean }>`
    && {
        display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
    }
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    flex-direction: row;
    flex-wrap: nowrap;
`
const ActionGroup = styled.div`
    && {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
    }
    align-items: center;
    gap: 16px;
    flex-shrink: 0;

    & > * { flex: 0 0 auto; }
`
const ActionBtn = styled.button<{ $active?: boolean }>`
    background: transparent;
    border: none;
    border-radius: 4px;
    width: 22px;
    height: 22px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.12s;
    padding: 0;
    flex-shrink: 0;

    svg { width: 14px; height: 14px; flex-shrink: 0; }
    svg.svg { width: 14px; height: 14px; }

    &:hover:not(:disabled) { opacity: 0.8; }
    &:disabled {
        opacity: 0.4;
        cursor: default;
    }
`
const MenuWrap = styled.div<{ $visible?: boolean }>`
    position: relative;
    flex-shrink: 0;
    display: ${({ $visible }) => ($visible ? 'block' : 'none')};
`

const SidebarToggleWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
`

const MenuBtn = styled.button`
    background: transparent;
    border: none;
    border-radius: 4px;
    width: 22px;
    height: 22px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: opacity 0.12s;

    &:hover { opacity: 0.8; }

    .svg {
        width: 14px;
        height: 14px;
    }
`

const MenuDropdown = styled.div`
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    background: #fff;
    border: 1px solid #e8eaed;
    border-radius: 10px;
    box-shadow: 0px 0px 6px rgba(51,57,81,0.1);
    padding: 10px;
    min-width: 220px;
    z-index: 1000;
`

const MenuItem = styled.button<{ $danger?: boolean; $disabled?: boolean }>`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border: none;
    background: transparent;
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
    text-align: left;
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    font-family: 'Nunito Sans', sans-serif;
    color: ${({ $danger }) => ($danger ? '#d14644' : '#41495e')};
    border-radius: 8px;

    svg, .svg { width: 14px; height: 14px; flex-shrink: 0; }

    &:hover { background: rgba(100,103,242,0.08); }
`

export default ContactDetails
